// ONESHOT §C-1 / §C0-5 — 단계별 리드폼 문구.
//
// ⚠️ **폼만 켜고 문구를 안 고치면 전환이 안 난다.**
// 공고 전 정비사업 현장에 `분양 정보 안내 신청` 을 붙이면 어색하다 —
// 분양가도 일정도 없는데 무엇을 안내받나. 실제로 전환이 난 현장
// (`울산 남구 달동 재개발`, 조합설립)은 **정보가 없어서 물어보려고** 신청한 것이다.
//
// ⚠️ 문구가 LeadForm · SiteActionBar · SiteDetailRail 세 곳에 흩어져 있었다.
//    한 곳에서만 고치면 화면마다 다른 말을 한다 — 여기 한 벌로 모은다.

import { UPCOMING_SALE_STAGES } from '@/lib/apt/lifecycle-label';
import { REGION_LEAD_SLUG_PREFIX } from '@/lib/apt/lead-eligibility';

/**
 * ⚠️ 'region' 은 leadCopyKind(stage) 가 «내지 않는다». 허브에는 단계가 없다 —
 *    호출부가 leadCopyForRegion() 으로 직접 고른다(home 과 같은 방식).
 */
export type LeadCopyKind = 'pre_notice' | 'offering' | 'existing' | 'home' | 'resale' | 'region' | 'region_hub';

export interface LeadCopy {
  /** 상단 라벨 밴드 */
  band: string;
  /** 폼 제목 · 하단 액션바 · 레일 버튼이 함께 쓴다 */
  cta: string;
  /** 폼 본문 한 줄 */
  lede: string;
  /** leads.inquiry_type — 미처리 경보가 '분양상담' 으로 대상을 좁힌다 */
  inquiryType: string;
  /**
   * 하단 액션바 버튼 문구. 안 주면 cta 를 그대로 쓴다.
   * ⚠️ cta 는 폼 «제목» 이자 버튼이다. 둘이 달라야 하는 경우에만 이걸 쓴다.
   * ⚠️ B8-2 이후 액션바는 «50/50 두 칸» 이다. 한 칸이 화면 폭의 절반(360px 폰에서
   *    약 164px)뿐이라 폼 제목 길이(「이 지역 분양 정보 안내 신청」 15자)는 두 줄로 접힌다.
   *    그래서 pre_notice·existing 도 여기서 «짧은 한 줄» 을 따로 준다.
   *    소비처는 SiteActionBar 하나다 — 레일·폼 제목은 cta 를 계속 쓴다.
   */
  button?: string;
  /**
   * hc3 L11 (2026-10-16 · 세션 A report/hc3/live) — 레일 진입 카드 본문 한 줄(SiteDetailRail).
   * 종전 레일은 단계와 무관하게 「잔여 세대·일정 직접 안내」 한 문장을 고정으로 썼다 —
   * 조합설립·시공사선정 단계(잔여 세대가 없다)와 기축 단지에 앞뒤가 안 맞았다. 단계별 한 줄을 여기 한 벌로 둔다.
   * 소비처가 이 값으로 바꾸는 것은 레인 e(SiteDetailRail) 몫 — 교차.
   */
  rail: string;
}

/**
 * 공고 전 정비사업 단계. 여기에 `분양` 이라는 말을 쓰지 않는다.
 * `contract_signing` 은 이미 분양이 끝난 계약 단계라 여기 넣지 않는다.
 */
// ⚠️ 여기서 목록을 «다시 적지 않는다». 같은 집합을 두 곳에 적으면 한쪽만 늘어난다 —
//    셀렉 카운트가 쓰는 UPCOMING_SALE_STAGES 와 «같은 상수» 여야 문구와 숫자가 갈리지 않는다.
const PRE_NOTICE = new Set<string>(UPCOMING_SALE_STAGES);

/**
 * 준공된 기축. 그 지역 분양 정보를 안내한다 — 이 단지를 파는 게 아니다.
 *
 * ⚠️ M5 §A: post_move_in · landmark_active 는 RESALE 로 옮겼다.
 *    그 둘은 이제 「이 단지 매물·시세」를 묻는다. 여기 남은 active_trade 는
 *    isLeadEligible 에도 leadKind 에도 없어 «현재 도달하지 않는다» — 나중에
 *    그 단계를 폼 대상으로 켤 때를 위해 문구만 남겨 둔다.
 */
const EXISTING = new Set(['active_trade']);

/** M5 §A — 준공돼 매매·전세가 도는 단지. 분양 문구를 재사용하지 않는다. */
const RESALE = new Set(['post_move_in', 'landmark_active']);

export function leadCopyKind(
  stage: string | null | undefined,
  opts: { redevInProgress?: boolean } = {},
): LeadCopyKind {
  const s = stage ?? '';
  // 판정 ②′(ii) — 기축이면서 «재건축이 진행 중» 이면 매물이 아니라 진행 상황을 묻는다.
  //   stage 는 post_move_in 이 맞다(철거 전이라 주민이 산다). 바꿀 것은 stage 가 아니라 문구다.
  //   ⚠️ RESALE 검사보다 «먼저» 온다 — 나중에 오면 post_move_in 이 resale 로 먼저 잡힌다.
  if (opts.redevInProgress) return 'pre_notice';
  if (PRE_NOTICE.has(s)) return 'pre_notice';
  if (RESALE.has(s)) return 'resale';
  if (EXISTING.has(s)) return 'existing';
  return 'offering';
}

/**
 * hc3 L11 — 공고 전 정비사업 본문(lede)의 «단계별» 판. 종전에는 단계와 무관하게
 * 「단계가 바뀌거나 시공사·세대수가 확정되면 알려드립니다.」 한 벌이라, 시공사가 이미 정해진
 * 착공 단계에도 「시공사 확정되면」을 약속했고 화면 네 곳 이상에서 같은 문장이 반복됐다.
 * 키에 없는 단계(조합설립 이전·모름·redevInProgress 기축)는 종전 문장 그대로다.
 * ⛔ 「분양」 이라는 말은 «일반분양 모집공고» 사실을 가리킬 때만 쓴다(정비판 규칙 — 분양 «안내» 약속 금지).
 */
const PRE_NOTICE_LEDE_BY_STAGE: Readonly<Record<string, string>> = {
  constructor_selected: '사업시행인가 등 다음 단계로 넘어가거나 세대수가 확정되면 알려드립니다. 분양가와 일정은 모집공고가 나와야 확정됩니다.',
  plan_approved: '관리처분인가 등 다음 단계 소식이 나오면 알려드립니다. 분양가와 일정은 모집공고가 나와야 확정됩니다.',
  mgmt_approved: '이주·철거 진행과 일반분양 모집공고 소식이 나오면 알려드립니다. 분양가와 일정은 모집공고가 나와야 확정됩니다.',
  construction: '일반분양 모집공고와 분양가가 나오면 알려드립니다. 확정되기 전 내용은 확정으로 안내하지 않습니다.',
};

/** hc3 L11 — 레일 한 줄의 공고 전 정비판 단계별 판. 키 밖은 COPY.pre_notice.rail. */
const PRE_NOTICE_RAIL_BY_STAGE: Readonly<Record<string, string>> = {
  construction: '일반분양 모집공고 소식을 담당자가 정리해 연락드립니다.',
  mgmt_approved: '이주·철거와 일반분양 일정 소식을 담당자가 정리해 연락드립니다.',
};

const COPY: Record<LeadCopyKind, Omit<LeadCopy, 'cta'> & { cta: (name: string) => string }> = {
  pre_notice: {
    band: '진행 상황 알림 · 무료',
    cta: () => '이 구역 진행 상황 알림 받기',
    lede: '단계가 바뀌거나 시공사·세대수가 확정되면 알려드립니다. 분양가와 일정은 모집공고가 나와야 확정됩니다.',
    // 아직 분양이 아니다. 미처리 경보의 '분양상담' 집계와 섞이면 응대 우선순위가 흐려진다.
    inquiryType: '진행상황알림',
    // 여기에 '분양' 을 쓰지 않는 규칙은 짧은 문구에서도 그대로다.
    button: '진행 상황 알림',
    rail: '단계 변경·시공사 선정 소식을 담당자가 정리해 연락드립니다.',
  },
  offering: {
    band: '분양 정보 안내 · 무료',
    cta: () => '분양 정보 안내 신청',
    lede: '담당자가 직접 연락드려 잔여 세대·일정을 안내합니다.',
    inquiryType: '분양상담',
    // 분양 중 단계 — 종전 레일 고정 문장 그대로(이 단계에서만 사실과 맞는다).
    rail: '잔여 세대·일정을 담당자가 직접 안내해 드립니다.',
  },
  existing: {
    band: '지역 분양 정보 · 무료',
    cta: () => '이 지역 분양 정보 안내 신청',
    lede: '이 단지는 입주가 끝난 단지입니다. 같은 지역에서 새로 나오는 분양 정보를 안내해 드립니다.',
    inquiryType: '분양상담',
    // 「이 지역」은 폼 제목에서 말한다. 반 칸짜리 버튼에서는 동작만 남긴다.
    button: '분양 정보 안내 신청',
    rail: '같은 지역에서 새로 나오는 분양 정보를 담당자가 안내해 드립니다.',
  },
  /**
   * M5 §A — 기축 매물·시세.
   *
   * ⚠️ 분양 문구를 재사용하지 않는다. 입주 2년 지난 아파트에
   *    「모집공고·분양가가 확정되면 알려드립니다」는 앞뒤가 안 맞는다.
   *    이 사람은 «지금 사고팔려고» 들어온 사람이다.
   * ⚠️ inquiryType 이 폼 종류를 가르는 유일한 값이다. channel 에 넣지 않는다 —
   *    거긴 유입 경로(organic·utm:kakao) 컬럼이라 섞으면 유입 분석이 깨진다.
   */
  /**
   * L1 D4 — 「매물상담」 유형 «폐지». 카더라는 중개·매물 상담을 하지 않는다(리드는 운영자만 본다 · D1).
   *    기축 단지에는 «같은 지역 분양·입주 소식 알림» 을 붙인다. inquiryType 은 '분양상담'(기존 집계·경보 집합).
   *    leads.inquiry_type 의 과거 '매물상담' 행은 그대로 둔다(열·데이터 불변 — 선택지만 없어진다).
   * ⛔ 「매물·시세」「담당자가 연락」 어휘를 쓰지 않는다.
   */
  resale: {
    band: '지역 분양·입주 소식 알림 · 무료',
    cta: () => '같은 지역 분양·입주 소식 알림 받기',
    lede: '이 단지와 같은 지역에 새로 나오는 분양·입주 소식을 알려드립니다. 청약·계약 신청이 아닙니다.',
    inquiryType: '분양상담',
    button: '분양 소식 알림',
    rail: '같은 지역에 새로 나오는 분양·입주 소식을 알려드립니다.',
  },
  /**
   * H1-3 홈 리드폼. **현장이 특정되지 않은 유일한 자리**다.
   * 다른 셋은 「이 현장」을 전제로 말하지만 여기서는 그럴 수 없다 —
   * 「이 구역」·「이 단지」 같은 지시어를 쓰면 홈에서 가리키는 대상이 없다.
   */
  home: {
    band: '분양 정보 안내 · 무료',
    cta: () => '관심 현장 분양 정보 받기',
    lede: '모집공고·분양가·잔여세대가 확정되면 담당자가 먼저 알려드립니다.',
    inquiryType: '분양상담',
    rail: '모집공고·분양가가 확정되면 담당자가 먼저 알려드립니다.',
  },
  /**
   * CPL §4 레인 B-1 — 허브(`/apt/redev/{지역}`) 리드폼.
   *
   * ⚠️ 「이 구역」·「이 현장」 같은 지시어를 쓰지 않는다. 허브가 가리키는 것은
   *    «한 구역» 이 아니라 그 지역의 구역 «전부» 다 — 홈(home)과 같은 이유다.
   *    대신 지역 이름을 받아 무엇에 대한 알림인지 분명히 한다.
   * ⚠️ 약속과 실제를 맞춘다(지시서 §4 B-1 규율 4). 이 폼이 실제로 하는 일은
   *    «담당자 연락» 이다 — 자동 알림 서비스가 아니다. 문구가 그렇게 말해야 한다.
   * ⚠️ inquiryType 은 '진행상황알림' 이다. 허브는 모집공고 전 구역이 대부분이라
   *    '분양상담' 으로 섞으면 미처리 24시간 경보의 대상 집합이 흐려진다
   *    (pre_notice 가 같은 이유로 갈라져 있다).
   */
  region: {
    band: '구역 진행상황 알림 · 무료',
    cta: (name: string) => (name ? `${name} 구역 진행상황 알림 신청` : '구역 진행상황 알림 신청'),
    lede: '관심 구역의 단계 변경·시공사 선정·세대수 확정 소식이 나오면 담당자가 정리해 연락드립니다. 분양가와 일정은 모집공고가 나와야 확정됩니다.',
    inquiryType: '진행상황알림',
    button: '진행 상황 알림',
    rail: '구역 단계 변경·시공사 선정 소식을 담당자가 정리해 연락드립니다.',
  },
  /**
   * CPL v4 §7 레인 B-2 — 지역 허브(`/apt/region/{시도}` · `/apt/area/{시도}/{구}`).
   *
   * ⚠️ region(정비 허브)과 «같은 말을 하지 않는다». `/apt/redev/{지역}` 은 정비구역 목록이라
   *    「구역 진행상황」이 맞지만, 지역 허브가 싣는 것은 청약·분양가·미분양·잔여세대다.
   *    거기에 「구역 진행상황 알림」을 붙이면 목록과 폼이 서로 다른 것을 가리킨다.
   * ⚠️ inquiryType 은 '분양상담' 이다 — «분리하지 않는다» (판정 2026-09-23, 정정분).
   *    한때 '지역분양안내' 로 갈랐다가 되돌렸다. 이유:
   *      ① 미처리 24시간 경보가 '분양상담' 으로 대상을 좁히는데 그 필터는 이 레포에 없다
   *         (Apps Script 또는 DB 함수 · §12 비범위). 새 값을 만들면 «경보에서 빠질 수» 있고,
   *         그건 곧 연락 누락이다. 실제 분양 문의이므로 그 집합에 들어가는 게 맞다.
   *      ② 허브 리드를 «성과로 가르는 축» 은 이미 있다 — `site_slug` 접두(`region:` · `area:` ·
   *         `redev:`)다. 집계는 그걸로 한다. 같은 구분을 두 열에 두지 않는다(한 사실은 한 번).
   *    (region=정비 허브는 공고 전 구역이 대부분이라 '진행상황알림' 으로 갈라 둔 것이고, 그건 문구도 다르다.)
   * ⚠️ 「이 지역」 같은 지시어 대신 지역 이름을 받는다 — 시·도면 `부산`, 시군구면 `부산 사상구`.
   *    호출부가 사람이 읽는 형태를 그대로 넘긴다(interest_region 규약과 같은 문자열이다).
   */
  region_hub: {
    band: '분양·미분양 안내 · 무료',
    cta: (name: string) => (name ? `${name} 분양·미분양 안내 신청` : '분양·미분양 안내 신청'),
    lede: '이 지역에 새로 나오는 분양과 미분양·잔여세대 물량을 담당자가 정리해 연락드립니다. 분양가와 일정은 모집공고가 나와야 확정됩니다.',
    inquiryType: '분양상담',
    button: '분양·미분양 안내',
    rail: '새 분양과 미분양·잔여세대 물량을 담당자가 정리해 연락드립니다.',
  },
};

/**
 * Q-8 — 공고 전 문구의 «비정비» 판. 「이 구역」은 정비구역을 가리키는 말이다.
 * 분양예정 신규 현장(SKY.V 센텀 등)·기분양 공사 중 현장에 「이 구역 진행 상황」은 대상이 틀린다.
 * ⚠️ inquiryType 은 정비판과 «같다»(진행상황알림) — 미처리 경보의 '분양상담' 집계와 섞지 않는 규칙은 그대로다.
 */
const PRE_NOTICE_SITE: Omit<LeadCopy, 'cta'> & { cta: (name: string) => string } = {
  band: '분양 소식 알림 · 무료',
  cta: () => '이 현장 분양 소식 알림 받기',
  lede: '모집공고·분양가·일정이 나오면 알려드립니다. 확정되기 전 내용은 확정으로 안내하지 않습니다.',
  inquiryType: '진행상황알림',
  button: '분양 소식 알림',
  rail: '모집공고·분양가·일정이 나오면 담당자가 먼저 알려드립니다.',
};

/**
 * @param opts.redev 정비사업 현장인가(site_type='redevelopment' 또는 redev 원천 연결).
 *   ⚠️ 모르면(undefined) 예전 문구(정비판)를 그대로 낸다 — 호출부가 «아는 곳에서만» 갈라 쓴다.
 */
export function leadCopy(stage: string | null | undefined, siteName = '', opts: { redev?: boolean; redevInProgress?: boolean } = {}): LeadCopy {
  const kind = leadCopyKind(stage, { redevInProgress: opts.redevInProgress });
  const site = kind === 'pre_notice' && opts.redev === false;
  const c = site ? PRE_NOTICE_SITE : COPY[kind];
  // hc3 L11 — 공고 전 정비판만 단계별 문장(redevInProgress 기축은 stage 가 post_move_in 이라 키 밖 → 종전 문장).
  const st = String(stage ?? '');
  const byStage = kind === 'pre_notice' && !site;
  const lede = (byStage && PRE_NOTICE_LEDE_BY_STAGE[st]) || c.lede;
  const rail = (byStage && PRE_NOTICE_RAIL_BY_STAGE[st]) || c.rail;
  return { band: c.band, cta: c.cta(siteName), lede, inquiryType: c.inquiryType, button: c.button, rail };
}

/** 홈 전용 한 벌. 단계가 없으므로 stage 로 고르지 않는다. */
export function leadCopyForHome(): LeadCopy {
  const c = COPY.home;
  return { band: c.band, cta: c.cta(''), lede: c.lede, inquiryType: c.inquiryType, button: c.button, rail: c.rail };
}

/**
 * B-1 허브 전용 한 벌. 홈과 같은 이유로 stage 로 고르지 않는다.
 * @param regionName `부산`·`서울` 같은 시·도 이름. 비면 지역 없는 판으로 떨어진다.
 */
export function leadCopyForRegion(regionName = ''): LeadCopy {
  const c = COPY.region;
  return { band: c.band, cta: c.cta(regionName), lede: c.lede, inquiryType: c.inquiryType, button: c.button, rail: c.rail };
}

/**
 * B-2 지역 허브 전용 한 벌.
 * @param regionName 시·도면 `부산`, 시군구면 `부산 사상구`.
 */
export function leadCopyForRegionHub(regionName = ''): LeadCopy {
  const c = COPY.region_hub;
  return { band: c.band, cta: c.cta(regionName), lede: c.lede, inquiryType: c.inquiryType, button: c.button, rail: c.rail };
}

/**
 * 허브 한 벌 «고르기». 표면을 가르는 것은 site_slug 접두다 —
 * 허브가 셋이 됐으므로 「어느 허브인가」를 LeadForm 안에서 다시 판정하게 두지 않는다.
 * ⚠️ `redev:` 는 B-1 그대로다(문구 한 글자도 바뀌지 않는다). 그 외 허브가 region_hub 다.
 */
export function leadCopyForHub(siteSlug: string, regionName = ''): LeadCopy {
  return siteSlug.startsWith(REGION_LEAD_SLUG_PREFIX)
    ? leadCopyForRegion(regionName)
    : leadCopyForRegionHub(regionName);
}
